/**
 * Who is in the room, from presence and never from WebRTC tracks: when each
 * person joined and left, when they were last seen, and whether their camera,
 * mic and share are on.
 *
 * The source is the server's joins subcollection (joinedAt, leftAt,
 * lastSeenAt, cameraOn, micOn, sharing). Until it is there, the same fields
 * come from what is written today: the booking's `${side}JoinedAt`,
 * `${side}SeenAt` and `${side}LeftAt`, which the server's joinLiveLesson and
 * leaveLiveLesson stamp, and each side's own participant entry for camera,
 * mic and share (presenceFromToday). Pure, so it is unit tested.
 */

export interface Presence {
  joinedAt: string | null;
  leftAt: string | null;
  lastSeenAt: string | null;
  cameraOn: boolean | null;
  micOn: boolean | null;
  sharing: boolean;
  /** Their camera stopped by their phone, while they are away from the tab (iOS stops it); they are still in. */
  paused: boolean;
}

/** One state per person, each with its own look in the room. */
export type PersonState = 'not_joined' | 'camera_on' | 'camera_off' | 'camera_paused' | 'dropped' | 'left';

/**
 * Not seen for this long with no Leave: their connection dropped (decision
 * 50), not "left the call". The room checks in every 10 to 15 seconds, so
 * this is two check-ins missed. Away from the tab (paused) they are given
 * until PRESENCE_STALE_MS, as a phone in the background checks in no more.
 */
export const PRESENCE_DROPPED_MS = 35_000;

/** How often the room checks in with its camera, mic and share (joinLiveLesson). */
export const PRESENCE_CHECK_IN_MS = 15_000;

/** A presence not seen for this long has gone, Leave or not (the server's PRESENCE_STALE_MINUTES). */
export const PRESENCE_STALE_MS = 2 * 60_000;

const ms = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : NaN);

/**
 * The person's state: joined, seen since they last left; "left" only for a
 * Leave, and dropped when they stop checking in without one.
 */
export function personState(p: Presence | null, nowMs: number): PersonState {
  if (!p?.joinedAt) return 'not_joined';
  const seen = Math.max(ms(p.joinedAt), Number.isNaN(ms(p.lastSeenAt)) ? -Infinity : ms(p.lastSeenAt));
  if (p.leftAt && ms(p.leftAt) >= seen) return 'left';
  const quiet = nowMs - seen;
  if (quiet >= (p.paused ? PRESENCE_STALE_MS : PRESENCE_DROPPED_MS)) return 'dropped';
  if (p.cameraOn === false) return 'camera_off';
  return p.paused ? 'camera_paused' : 'camera_on';
}

/** In the call now, camera on or off. */
export function inCallNow(p: Presence | null, nowMs: number): boolean {
  const s = personState(p, nowMs);
  return s === 'camera_on' || s === 'camera_off' || s === 'camera_paused';
}

/** When they left: their Leave, or the last time they were seen when they went without one. */
export function leftAtOf(p: Presence | null): string | null {
  if (!p?.joinedAt) return null;
  if (p.leftAt && ms(p.leftAt) >= ms(p.lastSeenAt ?? p.joinedAt)) return p.leftAt;
  return p.lastSeenAt ?? p.joinedAt;
}

type Stamp = string | { toDate: () => Date } | null | undefined;
const iso = (v: Stamp): string | null =>
  v == null ? null : typeof v === 'string' ? v : v.toDate().toISOString();
const later = (a: string | null, b: string | null) => (!a ? b : !b ? a : ms(a) >= ms(b) ? a : b);

/** The joins subcollection's document, as the server writes it. */
export function presenceFromJoin(d: Record<string, unknown>): Presence {
  return {
    joinedAt: iso(d.joinedAt as Stamp), leftAt: iso(d.leftAt as Stamp), lastSeenAt: iso(d.lastSeenAt as Stamp),
    cameraOn: typeof d.cameraOn === 'boolean' ? d.cameraOn : null,
    micOn: typeof d.micOn === 'boolean' ? d.micOn : null,
    sharing: d.sharing === true,
    paused: d.paused === true,
  };
}

/**
 * Presence from what is written today: the booking's stamps for the side,
 * and the person's own participant entry in the pair's room. A participant
 * entry marked inactive (Leave, or the tab gone) counts as a leave when it
 * counts as a leave as of their last sighting.
 */
export function presenceFromToday(
  booking: Record<string, unknown> | null | undefined,
  side: 'coach' | 'player',
  entry: Record<string, unknown> | null | undefined,
): Presence {
  const joinedAt = iso(booking?.[`${side}JoinedAt`] as Stamp);
  const seen = later(iso(booking?.[`${side}SeenAt`] as Stamp), entry?.active === true ? iso(entry?.lastSeenAt as Stamp) : null);
  let leftAt = iso(booking?.[`${side}LeftAt`] as Stamp);
  // Marked inactive (Leave, or the tab gone): left as of their last sighting, at once rather than when it goes stale.
  if (entry && entry.active === false && joinedAt) leftAt = later(leftAt, later(seen, joinedAt));
  return {
    joinedAt,
    leftAt,
    lastSeenAt: seen,
    cameraOn: typeof entry?.videoEnabled === 'boolean' && entry.active !== false ? entry.videoEnabled as boolean : null,
    micOn: typeof entry?.audioEnabled === 'boolean' && entry.active !== false ? entry.audioEnabled as boolean : null,
    sharing: entry?.active !== false && entry?.sharing === true,
    paused: entry?.active !== false && entry?.paused === true,
  };
}

/**
 * Their own Leave, from their participant entry (inactive, with its leftAt):
 * it stands over anything stamped later on their side (a check-in already on
 * its way when they left), so the other side reads the leave at once, not
 * when the server's stamps catch up (finding 7). A rejoin marks the entry
 * active again, and then it says nothing.
 */
export function withEntryLeave(p: Presence, entry: Record<string, unknown> | null | undefined): Presence {
  if (!entry || entry.active !== false) return p;
  const left = iso(entry.leftAt as Stamp);
  if (!left || !p.joinedAt || ms(left) < ms(p.joinedAt)) return p;
  const leftAt = later(p.leftAt, left);
  const lastSeenAt = p.lastSeenAt && ms(p.lastSeenAt) > ms(leftAt!) ? leftAt : p.lastSeenAt;
  return { ...p, leftAt, lastSeenAt, sharing: false, paused: false };
}

/**
 * Their own join, from their participant entry (active, with the wrapper's
 * joinedAt and their 10 s check-ins): it stands over a leave or a missing
 * join on the server's side, which can land later. The handshake starts as
 * soon as the room is open, before the server's join is written, and a quick
 * Rejoin's join can be stamped before its leave; either way the other side
 * read "hasn't joined yet" or "left the call", with no picture, though the
 * call was up. An old entry (joined before their last leave) says nothing.
 */
export function withEntryJoin(p: Presence | null, entry: Record<string, unknown> | null | undefined): Presence | null {
  if (!entry || entry.active !== true) return p;
  const joined = iso(entry.joinedAt as Stamp);
  if (!joined) return p;
  const base: Presence = p ?? { joinedAt: null, leftAt: null, lastSeenAt: null, cameraOn: null, micOn: null, sharing: false, paused: false };
  if (base.leftAt && ms(joined) <= ms(base.leftAt)) return p;
  return {
    ...base,
    joinedAt: base.joinedAt ?? joined,
    lastSeenAt: later(base.lastSeenAt, later(joined, iso(entry.lastSeenAt as Stamp))),
    cameraOn: base.cameraOn ?? (typeof entry.videoEnabled === 'boolean' ? entry.videoEnabled : null),
    micOn: base.micOn ?? (typeof entry.audioEnabled === 'boolean' ? entry.audioEnabled : null),
  };
}

/**
 * Which presence speaks for them: the server's joins document once it has
 * their join; until then (or when it was written without one, say by the
 * server noting a drop) the booking's stamps and their entry, so a person
 * who was in never reads as "hasn't joined yet" (finding 8).
 */
export function pickPresence(join: Presence | null | undefined, today: Presence | null): Presence | null {
  if (join?.joinedAt) return join;
  if (!today?.joinedAt && join) return join;
  return today;
}

/** The stage's line for a person who is not on camera: their state, in words. */
export function stateLine(state: PersonState, first: string, leftTime: string | null): string | null {
  if (state === 'not_joined') return `${first} hasn't joined yet`;
  if (state === 'camera_off') return 'Camera off';
  if (state === 'camera_paused') return 'Camera paused';
  if (state === 'left') return leftTime ? `${first} left the call at ${leftTime}` : `${first} left the call`;
  if (state === 'dropped') return `${first}'s connection dropped. Waiting for them to come back.`;
  return null;
}

/** The left screen's line and its way on, from what the other person is doing. */
export function leftScreenLine(state: PersonState, first: string): { line: string; otherIn: boolean } {
  if (state === 'camera_on' || state === 'camera_off' || state === 'camera_paused') return { line: `${first} is still in the call`, otherIn: true };
  if (state === 'not_joined') return { line: `${first} hasn't joined yet`, otherIn: false };
  if (state === 'dropped') return { line: `${first}'s connection dropped`, otherIn: false };
  return { line: `${first} left too`, otherIn: false };
}

/**
 * The camera and mic as the other person tapped them, over the room's data
 * channel (job 8): the track itself only toggles `enabled` on the same
 * sender, so their feed is back the moment they tap, with no renegotiation;
 * presence comes back through the database a second later. For a few
 * seconds the tap's word stands over presence, then presence speaks again.
 */
export interface AvWire { t: 'av'; cam?: boolean; mic?: boolean }
export const AV_WIRE_MS = 3000;
export function isAvWire(data: unknown): data is AvWire {
  return !!data && typeof data === 'object' && (data as { t?: unknown }).t === 'av';
}
export function withAvWire(p: Presence | null, wire: { cam?: boolean; mic?: boolean; at: number } | null, nowMs: number): Presence | null {
  if (!p || !wire || nowMs - wire.at >= AV_WIRE_MS) return p;
  return { ...p, ...(wire.cam !== undefined ? { cameraOn: wire.cam } : {}), ...(wire.mic !== undefined ? { micOn: wire.mic } : {}) };
}
